/**
 * State machine for the stacked 2FA challenge on the sign-in page.
 *
 * BetterAuth's signIn.email() returns:
 *   - { data: { twoFactorRedirect: true, ... }, error: null } when 2FA is required
 *   - { data: <session>, error: null } on full success (no 2FA enrolled)
 *   - { data: null, error: { ... } } on bad credentials / locked / etc.
 *
 * We don't actually want to follow a redirect on 2FA-required; we want to
 * swap the form contents in-place to a 2FA-code input. This hook tracks
 * that state and exposes the methods to advance/reset.
 */
import { useState } from 'react';

export type TwoFactorMethod = 'totp' | 'otp' | 'backupCode';

export type SignInState =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | { kind: 'twoFactorRequired'; methods: TwoFactorMethod[] }
  | { kind: 'twoFactorVerifying' }
  | { kind: 'success' }
  | { kind: 'error'; message: string };

export function use2FAChallenge() {
  const [state, setState] = useState<SignInState>({ kind: 'idle' });

  return {
    state,
    startSubmitting: () => setState({ kind: 'submitting' }),
    twoFactorRequired: (methods: TwoFactorMethod[] = ['totp']) =>
      setState({ kind: 'twoFactorRequired', methods }),
    startVerifyingTwoFactor: () => setState({ kind: 'twoFactorVerifying' }),
    succeed: () => setState({ kind: 'success' }),
    fail: (message: string) => setState({ kind: 'error', message }),
    reset: () => setState({ kind: 'idle' }),
  };
}
